import { Grid, IconButton, InputAdornment, TextField, Typography } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { memo, useContext, useRef } from 'react';

import { ContextProvider } from '@/context/ProjectContext.jsx';

import { dayjs } from '@/utils/dayjsSetup.js';
import { getISODate } from '@/utils/helpers.js';

import { useStyles } from './MainDateStyles.jsx';

const MIN_DATE = '1900-01-01';

const HIDDEN_PICKER_STYLE = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '1px',
    height: '100%',
    padding: 0,
    border: 0,
    opacity: 0,
    pointerEvents: 'none',
};

const DateOfBirthField = ({
    separator,
    setDOB,
    dob,
    setError,
    error,
    errorMessage,
    placeHolderMonth,
    placeHolderYear,
    placeHolderDay,
    requiredDateField,
    mainHeading,
    mt,
    disabled = false,
    required = false,
    showCalendarButton = false,
    calendarButtonAriaLabel,
}) => {
    const classes = useStyles();

    const { languageGlobal } = useContext(ContextProvider);

    const pickerRef = useRef(null);
    const displayFormat = languageGlobal === 'en' ? 'DD/MM/YYYY' : 'DD.MM.YYYY';

    // Native <input type="date"> needs an ISO value
    const pickerValue = dob.length === 10 ? dob.split(/[./]/).reverse().join('-') : '';

    const openPicker = (event) => {
        const input = pickerRef.current;
        if (!input) return;

        if (typeof input.showPicker === 'function') {
            input.showPicker();
            return;
        }

        const trigger = event.currentTarget;
        input.click();
        trigger?.focus();
    };

    const errorUpdates = (formatedDate) => {
        const year = dayjs().diff(dayjs(formatedDate, 'YYYY-MM-DD'), 'years');
        const month = dayjs().diff(dayjs(formatedDate, 'YYYY-MM-DD'), 'months');
        const date = dayjs().diff(dayjs(formatedDate, 'YYYY-MM-DD'), 'days');
        const year1900 = dayjs().diff(dayjs('1900-01-01', 'YYYY-MM-DD'), 'days');

        if ((Math.sign(date) === -1 || date === 0) && month === 0 && year === 0) {
            setError(true);
        }
        if (Number.isNaN(year) || date > year1900 || Math.sign(year) === -1) {
            setError(true);
        }
        if (!dayjs(formatedDate, 'YYYY-MM-DD', true).isValid()) {
            setError(true);
        }
    };

    const handlePickerChange = (event) => {
        const iso = event.target.value;
        if (!iso) {
            setDOB('');
            setError(true);
            return;
        }

        setDOB(dayjs(iso, 'YYYY-MM-DD').format(displayFormat));
        setError(false);
        errorUpdates(iso);
    };

    const validateForm = (type) => {
        let formatedDate;
        if (dob.length >= 10) {
            formatedDate = getISODate(dob, languageGlobal);
        }

        switch (type) {
            case 'dob':
                setError(false);
                if (dob === '') {
                    setError(true);
                }
                errorUpdates(formatedDate);
                break;
            default:
                break;
        }
    };

    const checkDateFormat = (event) => {
        let text = event.target.value;
        let formatedDate;
        if (text.length >= 10) {
            formatedDate = getISODate(text, languageGlobal);
        }

        if (dob.length > text.length && (text.length === 5 || text.length === 2)) {
            setDOB(text.slice(0, text.length - 1));
            return;
        }
        if (dob.length < text.length && (text.length === 2 || text.length === 5)) {
            text += separator;
        }
        if (text.length > 10) {
            return;
        }
        setDOB(text);

        if (text.length === 10) {
            setError(false);
            errorUpdates(formatedDate);
            if (!dayjs(text, languageGlobal === 'en' ? 'DD/MM/YYYY' : 'DD.MM.YYYY', true).isValid()) {
                setError(true);
            }
        }
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12} style={{ position: 'relative' }}>
                    <TextField
                        id="dateofbirth"
                        autoComplete="off"
                        label={mainHeading}
                        value={dob
                            .replaceAll(languageGlobal === 'en' && '.', '/')
                            .replaceAll(languageGlobal === 'de' && '/', '.')
                            .replace(/[a-zA-Z]/, '')}
                        onChange={(e) => {
                            checkDateFormat(e);
                        }}
                        onBlur={() => validateForm('dob')}
                        variant="filled"
                        color="primary"
                        fullWidth
                        disabled={disabled}
                        required={required}
                        placeholder={placeHolderDay + separator + placeHolderMonth + separator + placeHolderYear}
                        style={{ marginTop: mt }}
                        classes={{
                            root: error ? classes.textFieldsError : classes.textFields,
                        }}
                        InputLabelProps={{
                            classes: {
                                root: classes.inputLabel,
                                focused: 'focused',
                                shrink: 'shrink',
                            },
                        }}
                        InputProps={{
                            classes: {
                                root: classes.filledInputRoot,
                                focused: classes.fieldFocused,
                            },
                            inputProps: { 'aria-required': required },
                            endAdornment: showCalendarButton ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={calendarButtonAriaLabel ?? mainHeading}
                                        edge="end"
                                        disabled={disabled}
                                        onClick={openPicker}
                                    >
                                        <CalendarTodayIcon />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        }}
                    />
                    {showCalendarButton && (
                        <input
                            ref={pickerRef}
                            type="date"
                            value={pickerValue}
                            min={MIN_DATE}
                            max={dayjs().format('YYYY-MM-DD')}
                            onChange={handlePickerChange}
                            tabIndex={-1}
                            aria-hidden="true"
                            style={HIDDEN_PICKER_STYLE}
                        />
                    )}
                </Grid>
            </Grid>
            {error && (
                <Typography className={classes.errorText} style={{ marginTop: 3 }}>
                    {requiredDateField && dob.length === 0 ? requiredDateField : errorMessage}
                </Typography>
            )}
        </>
    );
};

export default memo(DateOfBirthField);
